import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import AttachmentsComponent from "@features/vessel-call/vessel-call-status/components/AttachmentComponent/AttachmentsComponent";
import { useDownloadPortServiceDocument } from "@features/vessel-call/vessel-call-status/PortServices/hooks/useDownloadPortServiceDocument";
import { formatDateTime } from "@features/vessel-call/vessel-call-status/utils";
import type { LastTaskDecision } from "@services/port-services/types";
import { useTranslation } from "react-i18next";

interface ViewDetailsSideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  lastTaskDecisions?: LastTaskDecision[];
}

const LANG_BASE = "vessel-call.port-services";
const TEST_ID_BASE = "port-services-rejected-view-details";
const SERVICE = "bff-vessel-call";

const ViewDetailsSideDrawer = ({
  isOpen,
  onClose,
  lastTaskDecisions,
}: ViewDetailsSideDrawerProps) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadPortServiceDocument();

  const visibleTaskDecisions = lastTaskDecisions?.filter(
    (decision) =>
      decision.rejectionReason ||
      decision.rejectionAttachment ||
      decision.cancellationReason ||
      decision.comments ||
      decision.notes ||
      decision.externalNotes ||
      decision.externalAttachment ||
      decision.internalNotes ||
      decision.internalAttachment,
  );

  return (
    <div data-testid={`${TEST_ID_BASE}-sidedrawer`} className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onClose}
        keepMounted={false}
        title={t("common.view-details")}
        submitLabel={t("common.ok")}
        hideCancelButton={true}
      >
        <div className="flex flex-col gap-4 w-[400px] p-6">
          {visibleTaskDecisions?.map((decision, index) => {
            const createdAtText = decision.timestamp ? formatDateTime(decision.timestamp) : "-";
            return (
              <div key={`${decision.timestamp}`} className="flex flex-col gap-4">
                {decision.rejectionReason && (
                  <div className="col-span-full flex flex-col gap-2">
                    <CustomFormLabel
                      sx={{ fontSize: 16, fontWeight: 700 }}
                      data-testid={`${TEST_ID_BASE}-label`}
                    >
                      {t(`${LANG_BASE}.fields.rejection-reason`)}
                    </CustomFormLabel>
                    <div
                      className="text-sm whitespace-pre-wrap wrap-break-word"
                      data-testid={`${TEST_ID_BASE}-static-content`}
                    >
                      {decision.rejectionReason}
                    </div>
                  </div>
                )}
                {decision.rejectionAttachment && (
                  <AttachmentsComponent
                    data-testid={`${TEST_ID_BASE}-rejection-attachments`}
                    documentName={decision.rejectionAttachment.name || "-"}
                    documentType={t(`${LANG_BASE}.fields.rejection-attachments`)}
                    onDownload={() => {
                      if (decision.rejectionAttachment?.url) {
                        handleDownload(
                          SERVICE,
                          decision.rejectionAttachment.url,
                          decision.rejectionAttachment.name,
                        );
                      }
                    }}
                    onPreview={() => {
                      if (decision.rejectionAttachment?.url) {
                        handlePreview(SERVICE, decision.rejectionAttachment.url);
                      }
                    }}
                    langBase={LANG_BASE}
                  />
                )}
                {decision.cancellationReason && (
                  <div className="col-span-full flex flex-col gap-2">
                    <CustomFormLabel
                      sx={{ fontSize: 16, fontWeight: 700 }}
                      data-testid={`${TEST_ID_BASE}-label`}
                    >
                      {t(`${LANG_BASE}.fields.cancellation-reason`)}
                    </CustomFormLabel>
                    <div
                      className="text-sm whitespace-pre-wrap wrap-break-word"
                      data-testid={`${TEST_ID_BASE}-static-content`}
                    >
                      {decision.cancellationReason}
                    </div>
                  </div>
                )}
                {decision.comments && (
                  <div className="col-span-full flex flex-col gap-2">
                    <CustomFormLabel
                      sx={{ fontSize: 16, fontWeight: 700 }}
                      data-testid={`${TEST_ID_BASE}-label`}
                    >
                      {t(`${LANG_BASE}.fields.comments`)}
                    </CustomFormLabel>
                    <div
                      className="text-sm whitespace-pre-wrap wrap-break-word"
                      data-testid={`${TEST_ID_BASE}-static-content`}
                    >
                      {decision.comments}
                    </div>
                  </div>
                )}
                {decision.notes && (
                  <div className="col-span-full flex flex-col gap-2">
                    <CustomFormLabel
                      sx={{ fontSize: 16, fontWeight: 700 }}
                      data-testid={`${TEST_ID_BASE}-label`}
                    >
                      {t(`${LANG_BASE}.fields.notes`)}
                    </CustomFormLabel>
                    <div
                      className="text-sm whitespace-pre-wrap wrap-break-word"
                      data-testid={`${TEST_ID_BASE}-static-content`}
                    >
                      {decision.notes}
                    </div>
                  </div>
                )}
                {decision.externalNotes && (
                  <div className="col-span-full flex flex-col gap-2">
                    <CustomFormLabel
                      sx={{ fontSize: 16, fontWeight: 700 }}
                      data-testid={`${TEST_ID_BASE}-label`}
                    >
                      {t(`${LANG_BASE}.fields.external-notes`)}
                    </CustomFormLabel>
                    <div
                      className="text-sm whitespace-pre-wrap wrap-break-word"
                      data-testid={`${TEST_ID_BASE}-static-content`}
                    >
                      {decision.externalNotes}
                    </div>
                  </div>
                )}
                {decision.externalAttachment && (
                  <AttachmentsComponent
                    data-testid={`${TEST_ID_BASE}-external-attachments`}
                    documentName={decision.externalAttachment.name || "-"}
                    documentType={t(`${LANG_BASE}.fields.external-attachments`)}
                    onDownload={() => {
                      if (decision.externalAttachment?.url) {
                        handleDownload(
                          SERVICE,
                          decision.externalAttachment.url,
                          decision.externalAttachment.name,
                        );
                      }
                    }}
                    onPreview={() => {
                      if (decision.externalAttachment?.url) {
                        handlePreview(SERVICE, decision.externalAttachment.url);
                      }
                    }}
                    langBase={LANG_BASE}
                  />
                )}
                {decision.internalNotes && (
                  <div className="col-span-full flex flex-col gap-2">
                    <CustomFormLabel
                      sx={{ fontSize: 16, fontWeight: 700 }}
                      data-testid={`${TEST_ID_BASE}-label`}
                    >
                      {t(`${LANG_BASE}.fields.internal-notes`)}
                    </CustomFormLabel>
                    <div
                      className="text-sm whitespace-pre-wrap wrap-break-word"
                      data-testid={`${TEST_ID_BASE}-static-content`}
                    >
                      {decision.internalNotes}
                    </div>
                  </div>
                )}
                {decision.internalAttachment && (
                  <div className="mt-4">
                    <AttachmentsComponent
                      data-testid={`${TEST_ID_BASE}-internal-attachments`}
                      documentName={decision.internalAttachment.name || "-"}
                      documentType={t(`${LANG_BASE}.fields.internal-attachments`)}
                      onDownload={() => {
                        if (decision.internalAttachment?.url) {
                          handleDownload(
                            SERVICE,
                            decision.internalAttachment.url,
                            decision.internalAttachment.name,
                          );
                        }
                      }}
                      onPreview={() => {
                        if (decision.internalAttachment?.url) {
                          handlePreview(SERVICE, decision.internalAttachment.url);
                        }
                      }}
                      langBase={LANG_BASE}
                    />
                  </div>
                )}
                <div className="mt-4">
                  <p className="text-xs text-gray-500">
                    <span>{t("common.submittedBy")}</span>
                    <span className="font-semibold">
                      {decision.submittedBy ? `${decision.submittedBy}` : ""}
                    </span>
                    <span> / </span>
                    {decision.submittedByRole ? ` ${decision.submittedByRole}` : ""}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{createdAtText}</p>
                </div>
                {index < (visibleTaskDecisions?.length ?? 0) - 1 && (
                  <hr className="border-gray-200" />
                )}
              </div>
            );
          })}
        </div>
      </SideDrawer>
    </div>
  );
};

export default ViewDetailsSideDrawer;
